/**
 * Navigation component for Alpine.js
 * Handles the main menu and submenu interactions.
 */
export const navigation = () => ({
	/**
	 * @type {boolean}
	 * Indicates if the submenu is open.
	 */
	isOpen: false,

	/**
	 * @type {number|null}
	 * Timeout ID for closing the submenu after a delay.
	 */
	timeout: null,

	/**
	 * Runs once when the component initializes.
	 * Ensures only one submenu is open at a time.
	 */
	init() {
		document.addEventListener("navigation:open", (e) => {
			// Close immediately if another menu was opened
			if (e.detail !== this.$el) {
				this.isOpen = false;
				clearTimeout(this.timeout);
				this.timeout = null;
			}
		});
	},

	/**
	 * Opens the submenu immediately.
	 * @returns {void}
	 */
	open() {
		this.isOpen = true;
		clearTimeout(this.timeout);
		document.dispatchEvent(
			new CustomEvent("navigation:open", { detail: this.$el }),
		);
	},

	/**
	 * Closes the submenu with delay.
	 * @returns {void}
	 */
	closeWithDelay() {
		this.timeout = setTimeout(() => {
			this.isOpen = false;
			this.timeout = null;
		}, 300);
	},

	/**
	 * Closes the submenu when the Escape key is pressed.
	 * Focuses the first link in the current target.
	 * @param {KeyboardEvent} ev - The keyboard event triggering the close action.
	 *
	 * @returns {void}
	 */
	escapeClose(ev) {
		if (this.isOpen) {
			this.isOpen = false;
			const current = ev.currentTarget;
			current.getElementsByTagName("a")[0]?.focus();
		}
	},
});

export const header = () => ({
	/**
	 * @type {boolean}
	 * Indicates if the mobile menu is open.
	 */
	mobileMenuOpen: false,

	/**
	 * @type {boolean}
	 * Indicates if the search overlay is open.
	 */
	searchOpen: false,

	/**
	 * @type {boolean}
	 * Indicates if the BITV menu is open.
	 */
	bitvOpen: false,

	/**
	 * Toggles the mobile menu state.
	 * @returns {void}
	 */
	toggleMobileMenu() {
		this.mobileMenuOpen = !this.mobileMenuOpen;
		this.searchOpen = false;
		this.bitvOpen = false;
		if (this.mobileMenuOpen) {
			// Focus the first link in the mobile menu when opened
			const firstLink = document.querySelector("#mobile-menu a");

			if (firstLink) {
				setTimeout(() => {
					firstLink.focus();
				}, 100); // Delay to ensure the menu is fully rendered before focusing
			}
		}
	},
});
